/** Minimum tangent length so a short gap still leaves the card edge cleanly. */
const MIN_PULL = 24;
/** Fraction of the gap used as the tangent, so a long run still bends. */
const PULL_RATIO = 0.45;
/** Keep attachments off the rounded corners. */
const EDGE_PAD = 12;
/** Space between extra lines that join the same two cards. */
const LANE_GAP = 22;

function center(rect) {
  return { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
}

function clamp(value, lo, hi) {
  if (!(hi > lo)) return (lo + hi) / 2;
  return Math.min(hi, Math.max(lo, value));
}

function gapBetween(from, to) {
  return {
    x: Math.max(to.x - (from.x + from.w), from.x - (to.x + to.w)),
    y: Math.max(to.y - (from.y + from.h), from.y - (to.y + to.h)),
  };
}

/**
 * Side of `from` that faces `to`.
 * Prefer the axis with the open gap (side-by-side cards connect left/right
 * even when one sits a little higher).
 */
export function sideFacing(from, to) {
  const gap = gapBetween(from, to);
  const fromCenter = center(from);
  const toCenter = center(to);
  const dx = toCenter.x - fromCenter.x;
  const dy = toCenter.y - fromCenter.y;
  const horizontal = gap.x > gap.y || (gap.x === gap.y && Math.abs(dx) >= Math.abs(dy));
  if (horizontal) return dx >= 0 ? 'right' : 'left';
  return dy >= 0 ? 'bottom' : 'top';
}

const OUTWARD = {
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
  top: { x: 0, y: -1 },
  bottom: { x: 0, y: 1 },
};

function anchorOnSide(rect, side, toward) {
  const c = center(rect);
  const padX = Math.min(EDGE_PAD, rect.w / 2);
  const padY = Math.min(EDGE_PAD, rect.h / 2);
  if (side === 'left' || side === 'right') {
    return {
      x: side === 'left' ? rect.x : rect.x + rect.w,
      y: rect.h <= 1 ? c.y : clamp(toward.y, rect.y + padY, rect.y + rect.h - padY),
    };
  }
  return {
    x: rect.w <= 1 ? c.x : clamp(toward.x, rect.x + padX, rect.x + rect.w - padX),
    y: side === 'top' ? rect.y : rect.y + rect.h,
  };
}

function crossOf(point, side) {
  return side === 'left' || side === 'right' ? point.y : point.x;
}

function pointOnSide(rect, side, cross) {
  const pad = side === 'left' || side === 'right'
    ? Math.min(EDGE_PAD, rect.h / 2)
    : Math.min(EDGE_PAD, rect.w / 2);
  const origin = side === 'left' || side === 'right' ? rect.y : rect.x;
  const size = side === 'left' || side === 'right' ? rect.h : rect.w;
  const clamped = size <= 1
    ? origin + size / 2
    : clamp(cross, origin + pad, origin + size - pad);
  if (side === 'left' || side === 'right') {
    return { x: side === 'left' ? rect.x : rect.x + rect.w, y: clamped };
  }
  return { x: clamped, y: side === 'top' ? rect.y : rect.y + rect.h };
}

function otherCoord(rect, side) {
  const point = center(rect);
  return side === 'left' || side === 'right' ? point.y : point.x;
}

function compareAttachments(a, b) {
  if (a.otherCross !== b.otherCross) return a.otherCross - b.otherCross;
  if (a.arrow.id < b.arrow.id) return -1;
  if (a.arrow.id > b.arrow.id) return 1;
  return 0;
}

function paddedRange(rect, side) {
  if (side === 'left' || side === 'right') {
    const pad = Math.min(EDGE_PAD, rect.h / 2);
    return [rect.y + pad, rect.y + rect.h - pad];
  }
  const pad = Math.min(EDGE_PAD, rect.w / 2);
  return [rect.x + pad, rect.x + rect.w - pad];
}

/** Keep ports in the given order and at least a small gap apart. */
function spreadCoords(ideals, lo, hi) {
  const coords = ideals.slice();
  const count = coords.length;
  if (count <= 1) return coords;
  const room = Math.max(0, hi - lo);
  const gap = Math.min(LANE_GAP, room / (count - 1));
  for (let i = 1; i < count; i += 1) {
    coords[i] = Math.max(coords[i], coords[i - 1] + gap);
  }
  if (coords[count - 1] > hi) {
    const shift = coords[count - 1] - hi;
    for (let i = 0; i < count; i += 1) coords[i] -= shift;
  }
  if (coords[0] < lo) {
    const shift = lo - coords[0];
    for (let i = 0; i < count; i += 1) coords[i] += shift;
  }
  for (let i = 0; i < count; i += 1) coords[i] = clamp(coords[i], lo, hi);
  return coords;
}

function assignGroups(groups, rect, side, matched) {
  if (!groups.length) return;
  const [lo, hi] = paddedRange(rect, side);
  const ideals = groups.map(members => {
    const sum = members.reduce((total, att) => total + att.natural, 0);
    return sum / members.length;
  });
  const coords = spreadCoords(ideals, lo, hi);
  groups.forEach((members, index) => {
    for (const att of members) {
      matched.set(`${att.arrow.id}:${att.role}`, coords[index]);
    }
  });
}

/**
 * Pair arrivals and departures from top to bottom.
 * The nth incoming point shares its axis with the nth outgoing point,
 * so the lines do not cross.
 */
function matchThrough(arrivals, departures, rect, side, matched) {
  const incoming = arrivals.slice().sort(compareAttachments);
  const outgoing = departures.slice().sort(compareAttachments);
  if (!incoming.length || !outgoing.length) return;
  const count = Math.min(incoming.length, outgoing.length);
  const groups = [];
  for (let i = 0; i < count; i += 1) groups.push([incoming[i], outgoing[i]]);
  const rest = incoming.length > outgoing.length ? incoming : outgoing;
  for (let i = count; i < rest.length; i += 1) groups.push([rest[i]]);
  assignGroups(groups, rect, side, matched);
}

function layoutUnpaired(attachments, matched, rect) {
  const pending = attachments.filter(att => !matched.has(`${att.arrow.id}:${att.role}`));
  if (!pending.length) return;
  pending.sort(compareAttachments);
  assignGroups(pending.map(att => [att]), rect, pending[0].side, matched);
}

function hasCardArea(rect) {
  return Boolean(rect) && (rect.w > 1 || rect.h > 1);
}

/**
 * Where each end of this line sits.
 * The curve itself is unchanged. On a card that a line enters and another
 * line leaves, those two attachment points share one coordinate across the
 * card. Several lines are paired from top to bottom so they do not cross.
 *
 * @param {string} arrowId
 * @param {{ id: string, startId: string, endId: string }[]} arrows
 * @param {(id: string) => { x: number, y: number, w: number, h: number } | null} boundsOf
 * @returns {{ startCross: number, endCross: number } | null}
 */
export function connectorEnds(arrowId, arrows, boundsOf) {
  const prepared = [];
  for (const arrow of arrows || []) {
    if (!arrow?.id || !arrow.startId || !arrow.endId || arrow.startId === arrow.endId) continue;
    const startRect = boundsOf?.(arrow.startId);
    const endRect = boundsOf?.(arrow.endId);
    if (!hasCardArea(startRect) || !hasCardArea(endRect)) continue;
    const startSide = sideFacing(startRect, endRect);
    const endSide = sideFacing(endRect, startRect);
    const naturalStart = crossOf(anchorOnSide(startRect, startSide, center(endRect)), startSide);
    const naturalEnd = crossOf(anchorOnSide(endRect, endSide, center(startRect)), endSide);
    prepared.push({
      id: arrow.id,
      startId: arrow.startId,
      endId: arrow.endId,
      startRect,
      endRect,
      startSide,
      endSide,
      naturalStart,
      naturalEnd,
    });
  }

  const matched = new Map();
  const byShape = new Map();
  for (const arrow of prepared) {
    const startAtt = {
      arrow,
      role: 'start',
      shapeId: arrow.startId,
      side: arrow.startSide,
      natural: arrow.naturalStart,
      rect: arrow.startRect,
      otherCross: otherCoord(arrow.endRect, arrow.startSide),
    };
    const endAtt = {
      arrow,
      role: 'end',
      shapeId: arrow.endId,
      side: arrow.endSide,
      natural: arrow.naturalEnd,
      rect: arrow.endRect,
      otherCross: otherCoord(arrow.startRect, arrow.endSide),
    };
    for (const att of [startAtt, endAtt]) {
      const group = byShape.get(att.shapeId) || [];
      group.push(att);
      byShape.set(att.shapeId, group);
    }
  }

  for (const group of byShape.values()) {
    const rect = group[0].rect;
    for (const [sideA, sideB] of [['left', 'right'], ['top', 'bottom']]) {
      matchThrough(
        group.filter(att => att.side === sideA && att.role === 'end'),
        group.filter(att => att.side === sideB && att.role === 'start'),
        rect,
        sideA,
        matched
      );
      matchThrough(
        group.filter(att => att.side === sideB && att.role === 'end'),
        group.filter(att => att.side === sideA && att.role === 'start'),
        rect,
        sideA,
        matched
      );
    }
    for (const side of ['left', 'right', 'top', 'bottom']) {
      layoutUnpaired(group.filter(att => att.side === side), matched, rect);
    }
  }

  const me = prepared.find(arrow => arrow.id === arrowId);
  if (!me) return null;
  return {
    startCross: matched.get(`${me.id}:start`) ?? me.naturalStart,
    endCross: matched.get(`${me.id}:end`) ?? me.naturalEnd,
  };
}

function pull(distance) {
  return Math.max(MIN_PULL, Math.abs(distance) * PULL_RATIO);
}

export function cubicPoint(start, cp1, cp2, end, t) {
  const u = 1 - t;
  return {
    x: u * u * u * start.x
      + 3 * u * u * t * cp1.x
      + 3 * u * t * t * cp2.x
      + t * t * t * end.x,
    y: u * u * u * start.y
      + 3 * u * u * t * cp1.y
      + 3 * u * t * t * cp2.y
      + t * t * t * end.y,
  };
}

function withMid(curve) {
  return {
    ...curve,
    mid: cubicPoint(curve.start, curve.cp1, curve.cp2, curve.end, 0.5),
  };
}

/** Straight cubic used when neither end is a card. */
export function straightCubic(start, end) {
  return withMid({
    start: { x: start.x, y: start.y },
    cp1: {
      x: start.x + (end.x - start.x) / 3,
      y: start.y + (end.y - start.y) / 3,
    },
    cp2: {
      x: start.x + (2 * (end.x - start.x)) / 3,
      y: start.y + (2 * (end.y - start.y)) / 3,
    },
    end: { x: end.x, y: end.y },
    startSide: null,
    endSide: null,
  });
}

/**
 * Cubic between two rectangles. Tangents leave the facing edges straight,
 * and each end slides along its edge toward the other card.
 * `startCross` / `endCross` pin one end so it lines up with the other
 * connector on that card. They do not straighten the line.
 */
export function alignedCubicBetween(startRect, endRect, options = {}) {
  const startSide = sideFacing(startRect, endRect);
  const endSide = sideFacing(endRect, startRect);
  const endCenter = center(endRect);
  const startCenter = center(startRect);
  const start = options.startCross == null
    ? anchorOnSide(startRect, startSide, endCenter)
    : pointOnSide(startRect, startSide, options.startCross);
  const end = options.endCross == null
    ? anchorOnSide(endRect, endSide, startCenter)
    : pointOnSide(endRect, endSide, options.endCross);

  if (Math.hypot(end.x - start.x, end.y - start.y) < 1) {
    return straightCubic(start, { x: end.x + 1, y: end.y });
  }

  const startOut = OUTWARD[startSide];
  const endOut = OUTWARD[endSide];
  const startDistance = startSide === 'left' || startSide === 'right'
    ? end.x - start.x
    : end.y - start.y;
  const endDistance = endSide === 'left' || endSide === 'right'
    ? end.x - start.x
    : end.y - start.y;
  const startPull = pull(startDistance);
  const endPull = pull(endDistance);

  return withMid({
    start,
    cp1: {
      x: start.x + startOut.x * startPull,
      y: start.y + startOut.y * startPull,
    },
    cp2: {
      x: end.x + endOut.x * endPull,
      y: end.y + endOut.y * endPull,
    },
    end,
    startSide,
    endSide,
  });
}

export function cubicPath(start, cp1, cp2, end) {
  const n = value => Math.round(value * 100) / 100;
  return `M ${n(start.x)} ${n(start.y)} C ${n(cp1.x)} ${n(cp1.y)} ${n(cp2.x)} ${n(cp2.y)} ${n(end.x)} ${n(end.y)}`;
}

/** Pull the stroke back along the end tangent so the arrowhead tip sits on the edge. */
export function shortenEnd(end, cp2, amount) {
  const dx = end.x - cp2.x;
  const dy = end.y - cp2.y;
  const len = Math.hypot(dx, dy) || 1;
  return {
    x: end.x - (dx / len) * amount,
    y: end.y - (dy / len) * amount,
  };
}

export function arrowheadPath(tip, from, length, width) {
  const dx = tip.x - from.x;
  const dy = tip.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const bx = tip.x - ux * length;
  const by = tip.y - uy * length;
  const n = value => Math.round(value * 100) / 100;
  return `M ${n(tip.x)} ${n(tip.y)} L ${n(bx + px * width)} ${n(by + py * width)} L ${n(bx - px * width)} ${n(by - py * width)} Z`;
}
